import { useCallback, useEffect, useState } from "react";
import { nameProblem, autoFixName } from "@/lib/names";
import {
  updateUserRole, renameUser, inviteUser, listInvites, cancelInvite, deleteUser, listFacilityUsers,
  setFacilityUserSkills, setEmployeeId, numberStaffBySeniority, setUserBirthday, teamBirthdays,
} from "@/lib/api";
import { birthdayText, parseTypedBirthday } from "@/components/StaffBirthday";
import { useAuth } from "@/context/AuthContext";
import { rank, ROLE_LABELS, ROLE_DESCRIPTIONS } from "@/lib/roles";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, UserPlus, Crown, Shield, Mail, X, Trash2, Pencil } from "lucide-react";

const ROLE_STYLES = {
  master_admin: { text: "text-rose-300", bg: "bg-rose-500/10 border-rose-500/30", Icon: Crown },
  admin: { text: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30", Icon: Shield },
  manager: { text: "text-blue-300", bg: "bg-blue-500/10 border-blue-500/30", Icon: Users },
  user: { text: "text-zinc-300", bg: "bg-zinc-500/10 border-zinc-500/30", Icon: Users },
};

// Skills are assigned per user per facility. They drive every coach/rep/member-manager
// dropdown across the app. Keep this list in sync with backend ROSTER_SKILLS.
const SKILLS = [
  { key: "sales", label: "Sales" },
  { key: "accountability", label: "Accountability" },
  { key: "member_manager", label: "Member Manager" },
  { key: "large_group_coach", label: "Large Group Coaching" },
  { key: "SPT", label: "Semi Private Training" },
  { key: "PT", label: "1-1 Personal Training" },
  { key: "lead_nurture", label: "Lead Nurture" },
  { key: "nutrition_consult", label: "Nutrition Consult" },
  { key: "operations", label: "Operations" },
];
const SKILL_LABEL = Object.fromEntries(SKILLS.map((s) => [s.key, s.label]));

export default function UserManagement() {
  const { user: me, activeFacility } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [bdays, setBdays] = useState(null);         // staff birthdays at this gym (routes/staff_birthdays.py)

  const myRank = rank(me?.role);

  // Which roles can I assign to others?
  const assignableRoles = ["user", "manager", "admin", "master_admin"].filter((r) => rank(r) <= myRank);

  const refresh = useCallback(async () => {
    if (!activeFacility?.id) return;
    const [u, inv] = await Promise.all([
      listFacilityUsers(activeFacility.id),
      listInvites().catch(() => []),
    ]);
    setUsers(u);
    setInvites(inv);
    teamBirthdays(activeFacility.id).then(setBdays).catch(() => setBdays(null));
  }, [activeFacility?.id]);
  useEffect(() => { refresh(); }, [refresh]);

  const changeRole = async (u, next) => {
    try {
      await updateUserRole(u.user_id, next);
      toast.success(`${u.name} is now ${ROLE_LABELS[next]}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update role");
    }
  };

  const revokeInvite = async (email) => {
    if (!window.confirm(`Cancel invite for ${email}?`)) return;
    await cancelInvite(email);
    toast.success("Invite cancelled");
    refresh();
  };

  const editName = async (u) => {
    const next = window.prompt(`Full name for ${u.email} (first and last name):`, autoFixName(u.name || ""));
    if (next == null) return;
    const fixed = autoFixName(next);
    if (nameProblem(fixed)) return toast.error(`Full name: ${nameProblem(fixed)}`);
    try {
      await renameUser(u.user_id, fixed);
      toast.success(`Name saved: ${fixed}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the name");
    }
  };

  const editBirthday = async (u) => {
    const cur = bdays?.people?.[u.user_id];
    const now = cur ? `${cur.day}/${cur.month}${cur.year ? `/${cur.year}` : ""}` : "";
    const next = window.prompt(`Birthday for ${u.name}: day/month, or day/month/year (like 14/8 or 14/8/1995). Leave empty to clear.`, now);
    if (next == null) return;
    const dob = parseTypedBirthday(next);
    if (dob == null) return toast.error("Type it like 14/8 or 14/8/1995");
    try {
      await setUserBirthday(u.user_id, dob);
      toast.success(dob ? `Birthday saved for ${u.name}` : `Birthday cleared for ${u.name}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the birthday");
    }
  };

  // Typed on a member's phone when they pay cash at the desk (see Cash Payments).
  const editEmployeeId = async (u) => {
    const next = window.prompt(`Staff ID for ${u.name} (like LIF007; leave blank to give them the next free number):`, u.employee_id || "");
    if (next == null) return;
    try {
      const r = await setEmployeeId(u.user_id, next.trim() || null);
      toast.success(`Staff ID: ${r.employee_id}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the staff ID");
    }
  };

  // Staff IDs run LIF001, LIF002, ... by seniority: paste everyone's email, most senior first.
  const numberBySeniority = async () => {
    const text = window.prompt("Emails, most senior first, separated by commas. The first gets LIF001:", "");
    if (!text) return;
    const emails = text.split(/[\s,;]+/).filter(Boolean);
    if (!window.confirm(`Renumber ${emails.length} people LIF001 to LIF${String(emails.length).padStart(3, "0")}? Their old IDs stop working.`)) return;
    try {
      const r = await numberStaffBySeniority(emails);
      toast.success(`Numbered ${r.numbered} people${r.renumbered_others ? `; ${r.renumbered_others} others got new numbers` : ""}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not renumber");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})?\n\nThey'll be signed out immediately and detached from every facility. Their historical records are preserved.`)) return;
    try {
      await deleteUser(u.user_id);
      toast.success(`${u.name} removed`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove user");
    }
  };

  const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-8" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Access & privileges</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">User Management</h2>
          <p className="text-sm text-zinc-400 mt-1">Invite people, change access levels, and audit who can see what.</p>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger data-testid="user-management-dialog-trigger-103" asChild>
            <Button className="bg-blue-600 hover:bg-blue-500 rounded-full px-5" data-testid="invite-user-btn">
              <UserPlus className="h-4 w-4 mr-1.5" /> Invite user
            </Button>
          </DialogTrigger>
          <InviteDialog
            assignableRoles={assignableRoles}
            facility={activeFacility}
            onDone={() => { setInviteOpen(false); refresh(); }}
          />
        </Dialog>
      </div>

      {/* Role legend */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="role-legend">
        {["master_admin", "admin", "manager", "user"].map((r) => {
          const s = ROLE_STYLES[r];
          const Icon = s.Icon;
          return (
            <div key={r} className={`rounded-xl border p-4 ${s.bg}`}>
              <div className="flex items-center gap-2">
                <Icon className={`h-4 w-4 ${s.text}`} />
                <div className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${s.text}`}>{ROLE_LABELS[r]}</div>
              </div>
              <div className="text-xs text-zinc-400 mt-2 leading-snug">{ROLE_DESCRIPTIONS[r]}</div>
            </div>
          );
        })}
      </section>

      {/* Pending invites */}
      {invites.length > 0 && (
        <section data-testid="pending-invites">
          <div className="eyebrow mb-3">Pending invites</div>
          <div className="card-surface overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Invited role</th>
                  <th className="px-5 py-3 font-medium">Invited by</th>
                  <th className="px-5 py-3 font-medium">When</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => {
                  const s = ROLE_STYLES[inv.role] || ROLE_STYLES.user;
                  return (
                    <tr key={inv.email} className="border-b border-[#1a1a1a] last:border-0" data-testid={`invite-row-${inv.email}`}>
                      <td className="px-5 py-3.5 font-medium flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-zinc-500" />{inv.email}</td>
                      <td className="px-5 py-3.5"><Chip role={inv.role} /></td>
                      <td className="px-5 py-3.5 text-zinc-400 text-xs">{inv.invited_by_name || "—"}</td>
                      <td className="px-5 py-3.5 text-zinc-500 text-xs">{new Date(inv.at).toLocaleDateString()}</td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={() => revokeInvite(inv.email)} className="text-zinc-500 hover:text-rose-400" data-testid={`cancel-invite-${inv.email}`}>
                          <X className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {bdays && <TeamBirthdays data={bdays} isAdmin={myRank >= rank("admin")} />}

      <section>
        <div className="mb-3 flex items-center justify-between">
          <div className="eyebrow">Members ({users.length})</div>
          {myRank >= rank("master_admin") && (
            <button type="button" onClick={numberBySeniority} className="text-xs text-zinc-400 hover:text-white hover:underline">
              Number staff IDs by seniority
            </button>
          )}
        </div>
        <div className="card-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                <th className="px-5 py-3 font-medium">Person</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Staff ID</th>
                <th className="px-5 py-3 font-medium">Birthday</th>
                <th className="px-5 py-3 font-medium">Joined</th>
                <th className="px-5 py-3 font-medium">Current role</th>
                <th className="px-5 py-3 font-medium">Change role</th>
                <th className="px-5 py-3 font-medium">Skills @ this gym</th>
                <th className="px-5 py-3 font-medium text-right">Remove</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = u.user_id === me?.user_id;
                const targetRank = rank(u.role);
                const canEdit = !isMe && targetRank <= myRank;
                const canRemove = !isMe && targetRank <= myRank && myRank >= rank("admin");
                return (
                  <tr key={u.user_id} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8 border border-[#262626]">
                          <AvatarImage src={u.picture} alt={u.name} />
                          <AvatarFallback className="bg-[#171717] text-[11px]">{initials(u.name)}</AvatarFallback>
                        </Avatar>
                        <div className="font-medium">
                          {u.name} {isMe && <span className="text-[10px] text-zinc-500 ml-1.5">(you)</span>}
                          {u.employment_type === "part_time" && <span className="ml-1.5 rounded-full border border-sky-500/40 bg-sky-500/10 px-1.5 py-0.5 text-[10px] text-sky-300" data-testid={`part-time-${u.user_id}`}>Part-time</span>}
                          {nameProblem(u.name) && <span className="ml-2 text-[10px] text-rose-400">name not full</span>}
                          <button type="button" onClick={() => editName(u)} className="ml-1.5 align-middle text-zinc-500 hover:text-white"
                            aria-label={`Edit name for ${u.email}`} title="Edit name"><Pencil className="inline h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-zinc-400">{u.email}</td>
                    <td className="px-5 py-3.5 text-xs">
                      {myRank >= rank("master_admin") ? (
                        <button type="button" onClick={() => editEmployeeId(u)} className="font-mono text-zinc-200 hover:text-white hover:underline"
                          aria-label={`Edit staff ID for ${u.email}`} title="Edit staff ID">{u.employee_id || "—"}</button>
                      ) : <span className="font-mono text-zinc-400">{u.employee_id || "—"}</span>}
                    </td>
                    <td className="px-5 py-3.5 text-xs">
                      {bdays && (isMe || targetRank <= myRank) ? (
                        <button type="button" onClick={() => editBirthday(u)} className="text-zinc-200 hover:text-white hover:underline whitespace-nowrap"
                          aria-label={`Edit birthday for ${u.email}`} title="Edit birthday" data-testid={`birthday-${u.user_id}`}>
                          {birthdayText(bdays.people?.[u.user_id]) || <span className="text-zinc-600">Add</span>}
                        </button>
                      ) : <span className="text-zinc-400 whitespace-nowrap">{birthdayText(bdays?.people?.[u.user_id]) || "Not added"}</span>}
                    </td>
                    <td className="px-5 py-3.5 text-zinc-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3.5"><Chip role={u.role} /></td>
                    <td className="px-5 py-3.5">
                      {canEdit ? (
                        <Select data-testid="user-management-select-210" value={u.role || "user"} onValueChange={(v) => changeRole(u, v)}>
                          <SelectTrigger className="bg-[#0a0a0a] border-[#262626] h-8 w-40" data-testid={`role-select-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                            {(assignableRoles || []).map((r) => (
                              <SelectItem data-testid="user-management-select-item-216" key={r} value={r} className="focus:bg-[#1a1a1a]">{ROLE_LABELS[r]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-[11px] text-zinc-600">{isMe ? "Cannot change your own role" : "Higher rank"}</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <SkillsEditor
                        user={u}
                        facility={activeFacility}
                        canEdit={myRank >= rank("manager") && (isMe || targetRank <= myRank)}
                        onSaved={refresh}
                      />
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      {canRemove ? (
                        <button
                          onClick={() => removeUser(u)}
                          className="inline-flex items-center gap-1 text-[11px] text-rose-300 hover:text-rose-200 border border-rose-500/30 hover:border-rose-500/60 rounded-full px-2.5 py-1 hover:bg-rose-500/10 transition-colors"
                          data-testid={`remove-user-${u.user_id}`}
                        >
                          <Trash2 className="h-3 w-3" /> Remove
                        </button>
                      ) : (
                        <span className="text-[11px] text-zinc-700">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-10 text-center text-sm text-zinc-500">No users yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

// Next 30 days at this gym (day and month only), and who hasn't added theirs yet.
function TeamBirthdays({ data, isAdmin }) {
  const when = (d) => (d === 0 ? "today" : d === 1 ? "tomorrow" : `in ${d} days`);
  return (
    <section data-testid="team-birthdays">
      <div className="eyebrow mb-3">Team birthdays</div>
      <div className="card-surface p-5 space-y-3 text-sm">
        {data.upcoming.length === 0 ? (
          <div className="text-zinc-500 text-xs">No birthdays in the next 30 days.</div>
        ) : (
          <ul className="space-y-1.5">
            {data.upcoming.map((p) => (
              <li key={p.user_id} className="flex items-center justify-between gap-3">
                <span>🎂 {p.name}</span>
                <span className="text-xs text-zinc-400 whitespace-nowrap">{p.label}, {when(p.in_days)}</span>
              </li>
            ))}
          </ul>
        )}
        {data.missing_count > 0 && (
          <div className="text-xs text-amber-200/90 border-t border-[#1f1f1f] pt-3" data-testid="team-birthdays-missing">
            {data.missing_count} {data.missing_count === 1 ? "hasn't" : "haven't"} added theirs: {data.missing.map((m) => m.name).join(", ")}.
            {" "}They can add it at /profile?birthday=1, or you can add it below.
          </div>
        )}
        {isAdmin && data.missing_all_count != null && (
          <div className="text-[11px] text-zinc-500">Across every gym: {data.missing_all_count} staff without a birthday saved.</div>
        )}
      </div>
    </section>
  );
}

function Chip({ role }) {
  const s = ROLE_STYLES[role] || ROLE_STYLES.user;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 rounded-full border ${s.text} ${s.bg}`}>
      <Icon className="h-3 w-3" /> {ROLE_LABELS[role] || "User"}
    </span>
  );
}

// Per-facility skill editor. Reads/writes user_facilities.skills for the active facility only,
// so the same person can be, say, a Sales rep in Delhi and an Accountability coach in Mumbai.
function SkillsEditor({ user, facility, canEdit, onSaved }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(user.skills || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSelected(user.skills || []); }, [user.skills]);

  const toggle = (key) => setSelected((s) => (s.includes(key) ? (s || []).filter((x) => x !== key) : [...s, key]));

  const save = async () => {
    if (!facility?.id) return;
    setBusy(true);
    try {
      const r = await setFacilityUserSkills(facility.id, user.user_id, selected);
      const held = (r?.pending_approval || []).map((k) => SKILL_LABEL[k] || k);
      if (held.length) toast.success(`Skills updated for ${user.name}. ${held.join(" and ")} sent to Anish for approval.`);
      else toast.success(`Skills updated for ${user.name}`);
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save skills");
    } finally {
      setBusy(false);
    }
  };

  const currentSkills = user.skills || [];

  if (!canEdit) {
    if (currentSkills.length === 0) return <span className="text-[11px] text-zinc-600">—</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {currentSkills.map((k) => (
          <span key={k} className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 text-[10px] px-2 py-0.5">
            {SKILL_LABEL[k] || k}
          </span>
        ))}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger data-testid="user-management-dialog-trigger-313" asChild>
        <button
          className="inline-flex items-center gap-1 text-[11px] rounded-full border border-[#262626] hover:border-blue-500/40 hover:bg-blue-500/5 px-2.5 py-1 text-zinc-300"
          data-testid={`edit-skills-${user.user_id}`}
        >
          {currentSkills.length === 0 ? (
            <span className="text-zinc-500">+ Add skills</span>
          ) : (
            <>
              {currentSkills.slice(0, 3).map((k) => (
                <span key={k} className="rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 px-2 py-0.5 text-[10px]">
                  {SKILL_LABEL[k] || k}
                </span>
              ))}
              {currentSkills.length > 3 && (
                <span className="text-zinc-500">+{currentSkills.length - 3}</span>
              )}
            </>
          )}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-md" data-testid={`skills-dialog-${user.user_id}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Skills for {user.name}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500 mt-1">
          Tick every role this user plays at <span className="text-zinc-300">{facility?.name}</span>. They&apos;ll appear in the
          matching coach/rep dropdowns across Sales, Accountability and Member Manager only for the skills you pick here.
          1-1 Personal Training and Semi Private Training are added once Anish approves them.
        </p>
        <div className="grid grid-cols-2 gap-2 mt-3">
          {SKILLS.map(({ key, label }) => {
            const active = selected.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggle(key)}
                data-testid={`skill-toggle-${key}`}
                className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left text-xs transition ${
                  active
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                    : "border-[#262626] bg-[#0a0a0a] text-zinc-300 hover:bg-[#141414]"
                }`}
              >
                <span>{label}</span>
                <span className={`h-4 w-4 rounded-full border ${
                  active ? "border-emerald-400 bg-emerald-500" : "border-[#333]"
                }`} />
              </button>
            );
          })}
        </div>
        <DialogFooter className="mt-2">
          <Button data-testid="user-management-button-366" variant="outline" onClick={() => setOpen(false)} className="border-[#262626] bg-transparent hover:bg-[#141414]">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid={`skills-save-${user.user_id}`}>
            {busy ? "Saving…" : "Save skills"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteDialog({ assignableRoles, facility, onDone }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState(assignableRoles.includes("user") ? "user" : assignableRoles[0]);
  const [skills, setSkills] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggleSkill = (key) => setSkills((s) => (s.includes(key) ? (s || []).filter((x) => x !== key) : [...s, key]));

  const submit = async (e) => {
    e.preventDefault();
    if (nameProblem(fullName)) return toast.error(`Full name: ${nameProblem(fullName)}`);
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Enter a valid email");
    if (!facility?.id) return toast.error("No active facility — pick one from the top-right switcher first");
    setBusy(true);
    try {
      // Scope the invite to the current facility so it never becomes a global cross-tenant grant.
      // Skills are stored on the user_facilities mapping that gets created when the invite is consumed.
      const res = await inviteUser(email.trim().toLowerCase(), role, facility.id, skills, autoFixName(fullName));
      if (res.existing) {
        toast.success(`Updated existing user's role to ${ROLE_LABELS[role]} for ${facility.name}`);
      } else {
        toast.success(`Invite sent for ${facility.name} · they'll join this gym when they sign in with Google`);
      }
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-md" data-testid="invite-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">Invite user{facility?.name ? ` to ${facility.name}` : ""}</DialogTitle>
      </DialogHeader>
      <form data-testid="user-management-form-411" onSubmit={submit} className="space-y-4 mt-2">
        {facility?.name && (
          <div className="rounded-md border border-blue-500/30 bg-blue-500/5 px-3 py-2 text-xs text-blue-200" data-testid="invite-facility-scope">
            This invite is scoped to <span className="font-semibold">{facility.name}</span> only. To grant access to other gyms, invite them from Global Admin.
          </div>
        )}
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Full name</Label>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} onBlur={() => fullName && setFullName(autoFixName(fullName))}
            placeholder="First and last name" className="bg-[#0a0a0a] border-[#262626] mt-1.5 mb-3" data-testid="invite-name" required />
          {fullName && nameProblem(fullName) && <p className="text-[11px] text-rose-400 -mt-2 mb-3">{nameProblem(fullName)}</p>}
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@yourgym.com"
            className="bg-[#0a0a0a] border-[#262626] mt-1.5"
            data-testid="invite-email-input"
            required
          />
          <p className="text-[11px] text-zinc-500 mt-1.5">They&apos;ll get the role below when they sign in with Google using this email.</p>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Assign role</Label>
          <Select data-testid="user-management-select-432" value={role} onValueChange={setRole}>
            <SelectTrigger className="bg-[#0a0a0a] border-[#262626] mt-1.5" data-testid="invite-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
              {(assignableRoles || []).map((r) => (
                <SelectItem data-testid="user-management-select-item-438" key={r} value={r} className="focus:bg-[#1a1a1a]">{ROLE_LABELS[r]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-zinc-500 mt-1.5">{ROLE_DESCRIPTIONS[role]}</p>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Skills (optional)</Label>
          <p className="text-[11px] text-zinc-500 mt-1 mb-2">
            Tick every hat they&apos;ll wear at this gym. They&apos;ll show up in the matching coach and rep dropdowns.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {SKILLS.map(({ key, label }) => {
              const active = skills.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleSkill(key)}
                  data-testid={`invite-skill-${key}`}
                  className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left text-xs transition ${
                    active
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                      : "border-[#262626] bg-[#0a0a0a] text-zinc-300 hover:bg-[#141414]"
                  }`}
                >
                  <span>{label}</span>
                  <span className={`h-4 w-4 rounded-full border ${active ? "border-emerald-400 bg-emerald-500" : "border-[#333]"}`} />
                </button>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="invite-submit-btn">
            {busy ? "Sending…" : "Send invite"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
